import React, { useState, useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Calendar, 
  Clock, 
  CreditCard, 
  Eye, 
  Loader2, 
  Share2,
  Sparkles,
  User, 
} from 'lucide-react';
import { Blog } from '../types/blog';
import { blogService } from '../services/blogService';
import { Navbar } from '../components/Navbar/Navbar';
import { Button } from '../components/common/Button';
// import { mockBlogs } from '../constants/mockBlogs';
import { trackButtonClick } from '../firebaseConfig';
import { useAnalyticsTracking } from '../hooks/useAnalyticsTracking';
import { ParticleBackground } from '../components/ParticleBackground';
import { Loader } from 'lucide-react';

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [relatedBlogs, setRelatedBlogs] = useState<Blog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [animationPhase, setAnimationPhase] = useState(0);
  const { trackSectionView: trackAnalyticsSection } = useAnalyticsTracking();
  
  useEffect(() => {
    if (!slug) return;
    // Start animation
    setTimeout(() => setAnimationPhase(1), 2100);
    
    // Force scroll to top only when slug changes
    window.scrollTo(0, 0);
    
    const fetchBlogData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [blogData, relatedData] = await Promise.all([
          blogService.getBlogBySlug(slug),
          blogService.getRelatedBlogs(slug, 3)
        ]);
        

        if (!blogData) {
          setError('Blog not found');
          setIsLoading(false);
          return;
        }

        setBlog(blogData);
        setRelatedBlogs(relatedData);

        // Track analytics - moved outside of state updates to prevent loop
        trackButtonClick('Blog Viewed', 'Blog Detail Page', { blog_slug: slug });
        
        // TODO: Increment view count when API is ready
        // await blogService.incrementViewCount(slug);

        // Apply SEO meta tags
        blogService.applyMetaTags(blogData);
      } catch (err) {
        console.error('Error fetching blog:', err);
        setError('Failed to load blog');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogData();
  }, [slug]); // Only depend on slug to prevent infinite loops

  // Track analytics separately to prevent render loops
  useEffect(() => {
    if (blog && !isLoading) {
      trackAnalyticsSection('Blog Detail Page', {
        blog_title: blog.title,
        blog_category: blog.categories[0] || 'uncategorized',
        blog_slug: slug
      });
    }
  }, [blog, isLoading]); // Only track when blog is loaded

  const handleShare = async () => {
    if (navigator.share && blog) {
      try {
        await navigator.share({
          title: blog.title,
          text: blog.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        // Fallback to copying URL
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    // You could add a toast notification here
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background-dark">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          {/* <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-light dark:border-primary-dark"></div> */}
          <Loader2 className="w-12 h-12 text-primary-light dark:text-primary-dark animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return <Navigate to="/blogs" replace />;
  }

  return (
    <>
      <Helmet>
        <title>{blog.title} | MockTrail Blog</title>
        <meta name="description" content={blog.metaDescription || blog.excerpt} />
        <meta name="keywords" content={blog.metaKeywords.join(', ')} />
        <meta name="author" content={`${blog.author.firstName} ${blog.author.lastName}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={blog.ogTitle || blog.title} />
        <meta property="og:description" content={blog.ogDescription || blog.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        {blog.featuredImage && <meta property="og:image" content={blog.featuredImage} />}
        <meta property="article:published_time" content={blog.publishedAt ? new Date(blog.publishedAt).toISOString() : ''} />
        <meta property="article:author" content={`${blog.author.firstName} ${blog.author.lastName}`} />
        <meta property="article:section" content={blog.categories[0]} />
        {blog.tags.map(tag => <meta key={tag} property="article:tag" content={tag} />)}
        
        {/* Twitter */}
        <meta name="twitter:card" content={blog.twitterCard || "summary_large_image"} />
        <meta name="twitter:title" content={blog.ogTitle || blog.title} />
        <meta name="twitter:description" content={blog.ogDescription || blog.excerpt} />
        {blog.featuredImage && <meta name="twitter:image" content={blog.featuredImage} />}
        
        {/* Canonical URL */}
        <link rel="canonical" href={blog.canonicalUrl || window.location.href} />
        
        {/* Structured Data */}
        {blog.structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(blog.structuredData)}
          </script>
        )}
      </Helmet>

      <div className="min-h-screen bg-background-light dark:bg-background-dark">
        {/* Background Effects */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
          <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow"></div>
          <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
        </div>
        
        <ParticleBackground />
        <Navbar />

        <main className="relative z-10 pt-48 pb-12 max-w-7xl mx-auto">
          <div className="container mx-auto px-4">

            <div className="max-w-7xl mx-auto">
              {/* Article Header */}
              <header className={`mb-12 transform transition-all duration-500 ease-out ${
                animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`} style={{ transitionDelay: '200ms' }}>
                {/* Categories */}
                {blog.categories.length > 0 && (
                  <div className="mb-4">
                    <Link
                      to={`/blogs?category=${encodeURIComponent(blog.categories[0])}`}
                      className="inline-block bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark px-3 py-1 rounded-full text-sm font-medium hover:bg-primary-light/15 dark:hover:bg-primary-dark/15 transition-colors duration-200"
                    >
                      {blog.categories[0]}
                    </Link>
                  </div>
                )}

                {/* Title */}
                <h1 className="text-4xl md:text-5xl leading-relaxed font-bold text-text-primary-light dark:text-text-primary-dark mb-6">
                  {blog.title}
                </h1>

                {/* Excerpt */}
                <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mb-4 leading-relaxed">
                  {blog.excerpt}
                </p>
                
                <div className="flex flex-col md:flex-row justify-between gap-4">
                {/* Meta Information */}
                <div className="flex flex-row flex-wrap justify-between my-2 md:my-0 items-center gap-6 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                  {blog.publishedAt && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-primary-light dark:text-primary-dark mb-0.5" />
                      <time dateTime={new Date(blog.publishedAt).toISOString()}>
                        {blogService.formatDate(blog.publishedAt)}
                      </time>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-primary-light dark:text-primary-dark" />
                    <span>{blog.readTimeMinutes} min read</span>
                  </div>

                  {blog.viewCount > 0 && (
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-primary-light dark:text-primary-dark" />
                      <span>{blog.viewCount.toLocaleString()} views</span>
                    </div>
                  )}
                </div>

                  <Button
                    variant="secondary"
                    size="sm"
                    className="py-3 px-6"
                    rightIcon={<Share2 className="w-4 h-4" />}
                    onClick={handleShare}
                  >
                   <span className="text-base">Share</span>
                 </Button>
                 </div>
              </header>

              {/* Featured Image */}
              {blog.featuredImage && (
                <div className={`mb-12 transform transition-all duration-500 ease-out ${
                  animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`} style={{ transitionDelay: '300ms' }}>
                  <div className="relative rounded-xl overflow-hidden shadow-lg">
                    <img
                      src={blog.featuredImage}
                      alt={blog.imageAlt || blog.title}
                      className="w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                </div>
              )}

              {/* Article Content */}
              <article className={`prose prose-lg dark:prose-invert max-w-none mb-12 transform transition-all duration-500 ease-out ${
                animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`} style={{ transitionDelay: '400ms' }}>
                <div 
                  className="blog-content"
                  dangerouslySetInnerHTML={{ __html: blog.content }}
                />
              </article>

              {/* Tags */}
              {blog.tags.length > 0 && (
                <div className={`mb-12 pt-8 border-t border-border-light dark:border-border-dark transform transition-all duration-500 ease-out ${
                  animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`} style={{ transitionDelay: '500ms' }}>
                  <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-4">
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {blog.tags.map(tag => (
                      <Link
                        key={tag}
                        to={`/blogs?tag=${encodeURIComponent(tag)}`}
                        className="px-3 py-1 text-sm bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark rounded-2xl hover:bg-secondary-light/15 dark:hover:bg-secondary-dark/15 transition-colors duration-200"
                      >
                        #{tag}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Author Section */}
              <div className={`mb-12 pt-8 border-t border-border-light dark:border-border-dark transform transition-all duration-500 ease-out ${
                animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`} style={{ transitionDelay: '600ms' }}>
                <div className="bg-card-light dark:bg-card-dark rounded-xl p-6 shadow-sm border border-border-light dark:border-border-dark">
                  <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                    {/* Author Logo */}
                    <div className="flex-shrink-0">
                      <img
                        src="/mocktrail-logo-bg-round.png"
                        alt="MockTrail Logo"
                        className="w-16 h-16 rounded-full shadow-md"
                      />
                    </div>
                    
                    {/* Author Info */}
                    <div className="flex-grow text-center md:text-left">
                      <h3 className="text-2xl md:text-xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
                        MockTrail - Practice Interviews With AI
                      </h3>
                      <p className="text-base text-text-secondary-light dark:text-text-secondary-dark mb-4 leading-relaxed max-w-3xl">
                        Master your interview skills with AI-powered mock interviews. Get real-time feedback, 
                        practice with industry-specific questions, and build confidence for your dream job.
                      </p>
                      <p className="w-fit flex items-center gap-2 text-sm bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark px-3 py-2 rounded-full text-sm font-medium hover:bg-primary-light/15 dark:hover:bg-primary-dark/15 transition-colors duration-200">
                        <Sparkles className="w-4 h-4" />
                        Get 20 minutes for free. No credit card required.
                      </p>
                    </div>
                    
                    {/* CTA Button */}
                    <div className="w-full md:w-auto flex-shrink-0 flex flex-col mt-2 gap-4">
                      <Button
                        variant="primary"
                        size="md"
                        className="px-7 py-4"
                        analyticsName="Start Practicing"
                        analyticsLocation="Author Section"
                        analyticsMetadata={{ section: 'author_section' }}
                        leftIcon={<User className="w-4 h-4" />}
                        onClick={() => {
                          trackButtonClick('Author CTA Clicked', 'Blog Detail Page', { 
                            blog_slug: slug,
                            cta_location: 'author_section' 
                          });
                          window.open('https://platform.mocktrail.app?utm_source=blog_detail_page&utm_medium=cta_button&utm_campaign=start_practicing', '_blank', 'noopener,noreferrer');
                        }}
                      >
                        Start For Free
                      </Button>
                      <Button
                        variant="secondary"
                        size="md"
                        analyticsName="View Pricing "
                        analyticsLocation="Author Section"
                        analyticsMetadata={{ section: 'author_section' }}
                        leftIcon={<CreditCard className="w-4 h-4" />}
                        onClick={() => {
                          trackButtonClick('View Pricing', 'Blog Detail Page', { 
                            blog_slug: slug,
                            cta_location: 'author_section' 
                          });
                          window.open('https://mocktrail.app/pricing?utm_source=blog_detail_page&utm_medium=cta_button&utm_campaign=view_pricing', '_blank', 'noopener,noreferrer');
                        }}
                      >
                        View Pricing
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>


          </div>
        </main>
      </div>
    </>
  );
} 